import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
const SalesManagerMappingPage = () => {
    const navigate = useNavigate();
    const [salesManagers, setSalesManagers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCustomerId, setEditingCustomerId] = useState(null);
    const [selectedManagerId, setSelectedManagerId] = useState('');
    // Access key management state
    const [showAccessKeyModal, setShowAccessKeyModal] = useState(false);
    const [selectedManager, setSelectedManager] = useState(null);
    const [customKeyInput, setCustomKeyInput] = useState('');
    const [accessKeyLoading, setAccessKeyLoading] = useState(false);
    const [showAccessKey, setShowAccessKey] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const fetchData = async () => {
        try {
            setLoading(true);
            // Fetch sales managers with access key info
            const smResponse = await fetch('/api/sales-managers', {
                credentials: 'include',
            });
            if (smResponse.status === 401) {
                navigate('/login');
                return;
            }
            const smData = await smResponse.json();
            // Fetch access key info for each sales manager
            const managersWithKeys = await Promise.all(smData.map(async (sm) => {
                try {
                    const keyRes = await fetch(`/api/sales-managers/${sm.id}/access-key`, {
                        credentials: 'include',
                    });
                    if (keyRes.ok) {
                        const keyData = await keyRes.json();
                        if (keyData.data) {
                            return {
                                ...sm,
                                access_key: keyData.data.access_key,
                                custom_key: keyData.data.custom_key,
                                key_is_active: keyData.data.is_active,
                                last_accessed_at: keyData.data.last_accessed_at,
                                access_count: keyData.data.access_count,
                                intel_last_accessed_at: keyData.data.intel_last_accessed_at,
                                intel_access_count: keyData.data.intel_access_count,
                                key_id: keyData.data.id,
                            };
                        }
                    }
                }
                catch (err) {
                    console.error('Error fetching access key:', err);
                }
                return sm;
            }));
            setSalesManagers(managersWithKeys);
            // Fetch customers
            const custResponse = await fetch('/api/customers', {
                credentials: 'include',
            });
            if (custResponse.ok) {
                const custData = await custResponse.json();
                setCustomers(custData.data || []);
            }
        }
        catch (error) {
            console.error('Error loading data:', error);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, []);
    const handleEditMapping = (customerId, currentManagerId) => {
        setEditingCustomerId(customerId);
        setSelectedManagerId(currentManagerId || '');
    };
    const handleSaveMapping = async (customerId) => {
        try {
            const customer = customers.find((c) => c.id === customerId);
            if (!customer)
                return;
            const response = await fetch(`/api/customers/${customerId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    ...customer,
                    sales_manager_id: selectedManagerId || null,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to update mapping');
            }
            await fetchData();
            setEditingCustomerId(null);
            setSelectedManagerId('');
        }
        catch (error) {
            alert(error.message || 'Failed to update mapping');
        }
    };
    const handleCancelEdit = () => {
        setEditingCustomerId(null);
        setSelectedManagerId('');
    };
    const getCustomersByManager = (managerId) => {
        return customers.filter((c) => c.sales_manager_id === managerId);
    };
    const getUnassignedCustomers = () => {
        return customers.filter((c) => !c.sales_manager_id);
    };
    // Access key management functions
    const handleOpenAccessKeyModal = (manager) => {
        setSelectedManager(manager);
        setCustomKeyInput(manager.custom_key || '');
        setShowAccessKey(false);
        setCopySuccess(false);
        setShowAccessKeyModal(true);
    };
    const handleGenerateKey = async () => {
        if (!selectedManager)
            return;
        setAccessKeyLoading(true);
        try {
            const response = await fetch(`/api/sales-managers/${selectedManager.id}/access-key`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to generate key');
            }
            const data = await response.json();
            // Update the manager in state
            setSalesManagers(prev => prev.map(sm => sm.id === selectedManager.id
                ? {
                    ...sm,
                    access_key: data.data.access_key,
                    custom_key: data.data.custom_key,
                    key_is_active: data.data.is_active,
                    last_accessed_at: data.data.last_accessed_at,
                    access_count: data.data.access_count,
                    key_id: data.data.id,
                }
                : sm));
            setSelectedManager(prev => prev ? {
                ...prev,
                access_key: data.data.access_key,
                custom_key: data.data.custom_key,
                key_is_active: data.data.is_active,
                last_accessed_at: data.data.last_accessed_at,
                access_count: data.data.access_count,
                key_id: data.data.id,
            } : null);
            setShowAccessKey(true);
        }
        catch (error) {
            alert(error.message || 'Failed to generate access key');
        }
        finally {
            setAccessKeyLoading(false);
        }
    };
    const handleSetCustomKey = async () => {
        if (!selectedManager || !customKeyInput)
            return;
        setAccessKeyLoading(true);
        try {
            const response = await fetch(`/api/sales-managers/${selectedManager.id}/access-key`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'setCustomKey', customKey: customKeyInput }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to set custom key');
            }
            const data = await response.json();
            // Update the manager in state
            setSalesManagers(prev => prev.map(sm => sm.id === selectedManager.id
                ? { ...sm, custom_key: data.data.custom_key }
                : sm));
            setSelectedManager(prev => prev ? { ...prev, custom_key: data.data.custom_key } : null);
            alert('Custom key set successfully!');
        }
        catch (error) {
            alert(error.message || 'Failed to set custom key');
        }
        finally {
            setAccessKeyLoading(false);
        }
    };
    const handleToggleKeyStatus = async () => {
        if (!selectedManager)
            return;
        setAccessKeyLoading(true);
        try {
            const response = await fetch(`/api/sales-managers/${selectedManager.id}/access-key`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'toggleStatus' }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to toggle key status');
            }
            const data = await response.json();
            // Update the manager in state
            setSalesManagers(prev => prev.map(sm => sm.id === selectedManager.id
                ? { ...sm, key_is_active: data.data.is_active }
                : sm));
            setSelectedManager(prev => prev ? { ...prev, key_is_active: data.data.is_active } : null);
        }
        catch (error) {
            alert(error.message || 'Failed to toggle key status');
        }
        finally {
            setAccessKeyLoading(false);
        }
    };
    const getPortalUrl = (key) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/sales-portal/${key}`;
    };
    const getIntelPortalUrl = (key) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/sales-intel-portal/${key}`;
    };
    const getLeadsPortalUrl = (key) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/sales-portal/${key}/leads`;
    };
    const getQuotesPortalUrl = (key) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/sales-portal/${key}/quotes`;
    };
    const [intelCopySuccess, setIntelCopySuccess] = useState(false);
    const [leadsCopySuccess, setLeadsCopySuccess] = useState(false);
    const [quotesCopySuccess, setQuotesCopySuccess] = useState(false);
    const handleCopyIntelLink = async () => {
        if (!selectedManager)
            return;
        const key = selectedManager.custom_key || selectedManager.access_key;
        const url = getIntelPortalUrl(key);
        try {
            await navigator.clipboard.writeText(url);
            setIntelCopySuccess(true);
            setTimeout(() => setIntelCopySuccess(false), 2000);
        }
        catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    const handleCopyLeadsLink = async () => {
        if (!selectedManager)
            return;
        const key = selectedManager.custom_key || selectedManager.access_key;
        const url = getLeadsPortalUrl(key);
        try {
            await navigator.clipboard.writeText(url);
            setLeadsCopySuccess(true);
            setTimeout(() => setLeadsCopySuccess(false), 2000);
        }
        catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    const handleCopyQuotesLink = async () => {
        if (!selectedManager)
            return;
        const key = selectedManager.custom_key || selectedManager.access_key;
        const url = getQuotesPortalUrl(key);
        try {
            await navigator.clipboard.writeText(url);
            setQuotesCopySuccess(true);
            setTimeout(() => setQuotesCopySuccess(false), 2000);
        }
        catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    const handleCopyLink = async () => {
        if (!selectedManager?.access_key && !selectedManager?.custom_key)
            return;
        const key = selectedManager.custom_key || selectedManager.access_key;
        const url = getPortalUrl(key);
        try {
            await navigator.clipboard.writeText(url);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
        catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };
    const formatDate = (timestamp) => {
        if (!timestamp)
            return 'Never';
        return new Date(timestamp * 1000).toLocaleString();
    };
    if (loading) {
        return (_jsx("div", { className: "min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8", children: _jsx("div", { className: "max-w-7xl mx-auto", children: _jsx("div", { className: "flex justify-center items-center h-64", children: _jsx("div", { className: "text-secondary-600", children: "Loading..." }) }) }) }));
    }
    return (_jsxs("div", { className: "min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8", children: [_jsxs("div", { className: "max-w-7xl mx-auto", children: [_jsxs("div", { className: "mb-8", children: [_jsx(Link, { to: "/dashboard", className: "text-primary-600 hover:text-primary-700 mb-4 inline-block", children: "\u2190 Back to Dashboard" }), _jsx("h1", { className: "text-3xl font-bold text-secondary-900", children: "Sales Manager - Customer Mapping" }), _jsx("p", { className: "text-secondary-600 mt-2", children: "View and manage customer assignments to sales managers" })] }), _jsxs("div", { className: "space-y-6", children: [salesManagers.map((manager) => {
                                const assignedCustomers = getCustomersByManager(manager.id);
                                return (_jsxs("div", { className: "card", children: [_jsx("div", { className: "border-b border-secondary-200 pb-4 mb-4", children: _jsxs("div", { className: "flex justify-between items-start", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-xl font-bold text-secondary-900", children: manager.name }), manager.email && (_jsxs("p", { className: "text-sm text-secondary-600", children: ["Email: ", manager.email] })), manager.phone && (_jsxs("p", { className: "text-sm text-secondary-600", children: ["Phone: ", manager.phone] }))] }), _jsx("button", { onClick: () => handleOpenAccessKeyModal(manager), className: `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${manager.access_key
                                                            ? manager.key_is_active
                                                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                                            : 'bg-primary-100 text-primary-700 hover:bg-primary-200'}`, children: manager.access_key
                                                            ? manager.key_is_active
                                                                ? '🔑 Portal Access Active'
                                                                : '🔒 Portal Access Disabled'
                                                            : '+ Generate Portal Access' })] }) }), _jsxs("div", { children: [_jsxs("h3", { className: "text-sm font-semibold text-secondary-700 mb-3", children: ["Assigned Customers (", assignedCustomers.length, ")"] }), assignedCustomers.length === 0 ? (_jsx("p", { className: "text-sm text-secondary-500 italic", children: "No customers assigned yet" })) : (_jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3", children: assignedCustomers.map((customer) => (_jsx("div", { className: "p-3 bg-primary-50 border border-primary-200 rounded-lg", children: _jsxs("div", { className: "flex justify-between items-start", children: [_jsxs("div", { className: "flex-1", children: [_jsx("p", { className: "font-medium text-secondary-900", children: customer.name }), customer.email && (_jsx("p", { className: "text-xs text-secondary-600 mt-1", children: customer.email }))] }), _jsx("button", { onClick: () => handleEditMapping(customer.id, customer.sales_manager_id), className: "text-xs text-primary-600 hover:text-primary-700", children: "Edit" })] }) }, customer.id))) }))] })] }, manager.id));
                            }), getUnassignedCustomers().length > 0 && (_jsxs("div", { className: "card bg-amber-50 border-amber-200", children: [_jsxs("div", { className: "border-b border-amber-300 pb-4 mb-4", children: [_jsx("h2", { className: "text-xl font-bold text-secondary-900", children: "Unassigned Customers" }), _jsx("p", { className: "text-sm text-secondary-600 mt-1", children: "Customers without a sales manager" })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3", children: getUnassignedCustomers().map((customer) => (_jsx("div", { className: "p-3 bg-white border border-amber-300 rounded-lg", children: _jsxs("div", { className: "flex justify-between items-start", children: [_jsxs("div", { className: "flex-1", children: [_jsx("p", { className: "font-medium text-secondary-900", children: customer.name }), customer.email && (_jsx("p", { className: "text-xs text-secondary-600 mt-1", children: customer.email }))] }), _jsx("button", { onClick: () => handleEditMapping(customer.id), className: "text-xs text-primary-600 hover:text-primary-700", children: "Assign" })] }) }, customer.id))) })] }))] })] }), editingCustomerId && (_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4", children: [_jsx("h3", { className: "text-lg font-bold text-secondary-900 mb-4", children: "Assign Sales Manager" }), _jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-secondary-700 mb-2", children: "Customer" }), _jsx("p", { className: "text-secondary-900", children: customers.find((c) => c.id === editingCustomerId)?.name })] }), _jsxs("div", { className: "mb-6", children: [_jsx("label", { className: "block text-sm font-medium text-secondary-700 mb-2", children: "Sales Manager" }), _jsxs("select", { value: selectedManagerId, onChange: (e) => setSelectedManagerId(e.target.value), className: "input w-full", children: [_jsx("option", { value: "", children: "None (Unassign)" }), salesManagers.map((sm) => (_jsxs("option", { value: sm.id, children: [sm.name, sm.territory ? ` - ${sm.territory}` : ''] }, sm.id)))] })] }), _jsxs("div", { className: "flex justify-end space-x-3", children: [_jsx("button", { onClick: handleCancelEdit, className: "btn btn-outline", children: "Cancel" }), _jsx("button", { onClick: () => handleSaveMapping(editingCustomerId), className: "btn btn-primary", children: "Save" })] })] }) })), showAccessKeyModal && selectedManager && (_jsx("div", { className: "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col", children: [_jsxs("div", { className: "flex justify-between items-start p-6 pb-4 border-b border-secondary-200 flex-shrink-0", children: [_jsxs("h3", { className: "text-lg font-bold text-secondary-900", children: ["Portal Access for ", selectedManager.name] }), _jsx("button", { onClick: () => setShowAccessKeyModal(false), className: "text-secondary-400 hover:text-secondary-600", children: "\u2715" })] }), _jsx("div", { className: "flex-1 overflow-y-auto p-6", children: selectedManager.access_key ? (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { className: `px-3 py-1 rounded-full text-sm font-medium ${selectedManager.key_is_active
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-red-100 text-red-700'}`, children: selectedManager.key_is_active ? '✓ Active' : '✕ Disabled' }), _jsx("button", { onClick: handleToggleKeyStatus, disabled: accessKeyLoading, className: "text-sm text-primary-600 hover:text-primary-700", children: selectedManager.key_is_active ? 'Disable' : 'Enable' })] }), _jsxs("div", { className: "bg-secondary-50 p-4 rounded-lg", children: [_jsxs("div", { className: "flex justify-between items-center mb-2", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700", children: "Access Key" }), _jsx("button", { onClick: () => setShowAccessKey(!showAccessKey), className: "text-xs text-primary-600 hover:text-primary-700", children: showAccessKey ? 'Hide' : 'Show' })] }), _jsx("code", { className: "text-sm font-mono text-secondary-900 break-all", children: showAccessKey
                                                    ? selectedManager.access_key
                                                    : '••••••••••••••••••••••••••••••••' })] }), selectedManager.custom_key && (_jsxs("div", { className: "bg-secondary-50 p-4 rounded-lg", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700 block mb-2", children: "Custom Key" }), _jsx("code", { className: "text-sm font-mono text-secondary-900", children: selectedManager.custom_key })] })), _jsxs("div", { className: "bg-primary-50 p-4 rounded-lg", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700 block mb-2", children: "\uD83D\uDCCA Pricing Portal Link" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "text-xs font-mono text-primary-700 break-all flex-1", children: getPortalUrl(selectedManager.custom_key || selectedManager.access_key) }), _jsx("button", { onClick: handleCopyLink, className: `px-3 py-1 rounded text-sm font-medium transition-colors ${copySuccess
                                                            ? 'bg-green-500 text-white'
                                                            : 'bg-primary-600 text-white hover:bg-primary-700'}`, children: copySuccess ? '✓ Copied!' : 'Copy' })] }), _jsxs("div", { className: "mt-2 grid grid-cols-2 gap-4 text-xs", children: [_jsxs("div", { children: [_jsx("span", { className: "text-secondary-500", children: "Last Accessed:" }), _jsx("p", { className: "text-secondary-700", children: formatDate(selectedManager.last_accessed_at) })] }), _jsxs("div", { children: [_jsx("span", { className: "text-secondary-500", children: "Total Accesses:" }), _jsx("p", { className: "text-secondary-700", children: selectedManager.access_count || 0 })] })] })] }), _jsxs("div", { className: "bg-cyan-50 p-4 rounded-lg", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700 block mb-2", children: "\uD83D\uDC65 Leads Portal Link" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "text-xs font-mono text-cyan-700 break-all flex-1", children: getLeadsPortalUrl(selectedManager.custom_key || selectedManager.access_key) }), _jsx("button", { onClick: handleCopyLeadsLink, className: `px-3 py-1 rounded text-sm font-medium transition-colors ${leadsCopySuccess
                                                            ? 'bg-green-500 text-white'
                                                            : 'bg-cyan-600 text-white hover:bg-cyan-700'}`, children: leadsCopySuccess ? '✓ Copied!' : 'Copy' })] })] }), _jsxs("div", { className: "bg-emerald-50 p-4 rounded-lg", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700 block mb-2", children: "\uD83D\uDCC4 Quotes Portal Link" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "text-xs font-mono text-emerald-700 break-all flex-1", children: getQuotesPortalUrl(selectedManager.custom_key || selectedManager.access_key) }), _jsx("button", { onClick: handleCopyQuotesLink, className: `px-3 py-1 rounded text-sm font-medium transition-colors ${quotesCopySuccess
                                                            ? 'bg-green-500 text-white'
                                                            : 'bg-emerald-600 text-white hover:bg-emerald-700'}`, children: quotesCopySuccess ? '✓ Copied!' : 'Copy' })] })] }), _jsxs("div", { className: "bg-orange-50 p-4 rounded-lg", children: [_jsx("span", { className: "text-sm font-medium text-secondary-700 block mb-2", children: "\uD83D\uDCC8 Intel Portal Link" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "text-xs font-mono text-orange-700 break-all flex-1", children: getIntelPortalUrl(selectedManager.custom_key || selectedManager.access_key) }), _jsx("button", { onClick: handleCopyIntelLink, className: `px-3 py-1 rounded text-sm font-medium transition-colors ${intelCopySuccess
                                                            ? 'bg-green-500 text-white'
                                                            : 'bg-orange-600 text-white hover:bg-orange-700'}`, children: intelCopySuccess ? '✓ Copied!' : 'Copy' })] }), _jsxs("div", { className: "mt-2 grid grid-cols-2 gap-4 text-xs", children: [_jsxs("div", { children: [_jsx("span", { className: "text-secondary-500", children: "Last Accessed:" }), _jsx("p", { className: "text-secondary-700", children: formatDate(selectedManager.intel_last_accessed_at) })] }), _jsxs("div", { children: [_jsx("span", { className: "text-secondary-500", children: "Total Accesses:" }), _jsx("p", { className: "text-secondary-700", children: selectedManager.intel_access_count || 0 })] })] })] }), _jsxs("div", { className: "border-t border-secondary-200 pt-4", children: [_jsx("label", { className: "block text-sm font-medium text-secondary-700 mb-2", children: "Set Custom Key (Optional)" }), _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { type: "text", value: customKeyInput, onChange: (e) => setCustomKeyInput(e.target.value), placeholder: "e.g., tom-hanks-2024", className: "input flex-1" }), _jsx("button", { onClick: handleSetCustomKey, disabled: accessKeyLoading || !customKeyInput, className: "btn btn-outline", children: "Set" })] }), _jsx("p", { className: "text-xs text-secondary-500 mt-1", children: "8-64 characters, letters, numbers, underscores, hyphens only" })] }), _jsx("div", { className: "border-t border-secondary-200 pt-4", children: _jsx("button", { onClick: handleGenerateKey, disabled: accessKeyLoading, className: "text-sm text-amber-600 hover:text-amber-700", children: "\u26A0\uFE0F Regenerate Access Key (invalidates current key)" }) })] })) : (
                            /* No Key Yet */
                            _jsxs("div", { className: "text-center py-6", children: [_jsx("p", { className: "text-secondary-600 mb-4", children: "No portal access key has been generated for this sales manager yet." }), _jsx("button", { onClick: handleGenerateKey, disabled: accessKeyLoading, className: "btn btn-primary", children: accessKeyLoading ? 'Generating...' : 'Generate Access Key' })] })) }), _jsx("div", { className: "p-6 pt-4 border-t border-secondary-200 flex-shrink-0 flex justify-end", children: _jsx("button", { onClick: () => setShowAccessKeyModal(false), className: "btn btn-outline", children: "Close" }) })] }) }))] }));
};
export default SalesManagerMappingPage;
